import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import Box from "@mui/material/Box";
import { useContentSetting } from "src/Hooks/ContentContext/ContentSettingState";
import { s3baseUrl } from "src/config/config";
import { coin } from "src/assets";
import ConfirmationWithDate from "src/components/_dashboard/programmes/ConfirmationWithDate";
import { gr } from "src/design-system/tokens";
import icons from "src/design-system/icons";
import GrPage from "src/design-system/components/GrPage";
import GrPageHeader from "src/design-system/components/GrPageHeader";
import GrMediaCard from "src/design-system/components/GrMediaCard";
import GrButton from "src/design-system/components/GrButton";
import GrIcon from "src/design-system/components/GrIcon";
import GrEmptyState from "src/design-system/components/GrEmptyState";
import GrLoader from "src/design-system/components/GrLoader";
import GrCard from "src/design-system/components/GrCard";
import {
  canOpenCalendar,
  coinsLabel,
  formatDay,
  htmlText,
  isLimited,
  splitIntro,
} from "./programmeHelpers";
import useProgrammeSearch from "./useProgrammeSearch";
import { ProgrammeLoadMore, ProgrammeNoMatch, ProgrammeSearchBar } from "./ProgrammeSearchParts";
import { isFirstRow } from "../Video/videoHelpers";

/**
 * Programmes (/programmes): the programmes this member has, as cards.
 * One button per card: Open programme (opens its calendar; or Start programme for a limited one not started yet);
 * the details open from the description's "View more".
 */

const gridSx = {
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr",
    sm: "repeat(2, minmax(0, 1fr))",
    lg: "repeat(3, minmax(0, 1fr))",
  },
  gap: gr.space[6],
};
// Date (calendar icon) on the left, coins (coins icon) on the right.
const metaSx = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "space-between",
  gap: gr.space[2],
};
const metaItemSx = { display: "inline-flex", alignItems: "center", gap: gr.space[1] };
// Coins look the same as in the Community Feed (the gold coin image), icon-sized.
const coinSx = { width: gr.icon.sm, height: gr.icon.sm, flex: "none" };
// Search (and the gym's button) on the right of the title; full width under it on phones.
const headerActionsSx = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: gr.space[3],
  width: { xs: "100%", sm: "auto" },
};
// The card's one button spans the card.
const cardButtonSx = { width: "100%" };

export default function Programmes() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { contentSettingData } = useContentSetting();
  const [startFor, setStartFor] = useState(null);
  // Open flag kept apart so the dialog keeps its programme while it fades out.
  const [startOpen, setStartOpen] = useState(false);
  // From the API, 50 at a time; the search runs in the database (Load more for the rest).
  const search = useProgrammeSearch("programmesList", (error) =>
    enqueueSnackbar(error?.message, { variant: "error" })
  );
  const { isLoading, refetch } = search;
  // No programmes at all (not just no match for a search): empty page, no search box.
  const noProgrammes = !isLoading && !search.searching && search.total === 0;

  const intro = splitIntro(contentSettingData?.programs_description);
  const buttonText = contentSettingData?.program_button_text;
  const buttonLink = contentSettingData?.program_button_link;

  const openCalendar = (item) =>
    navigate(`/programmes/calendar/${item?._id?.program_slug}`, { state: item });
  const openDetails = (item) =>
    navigate(`/programmes/${item?._id?.program_slug}`, { state: item });

  const meta = (item) => {
    let date = "";
    if (isLimited(item)) {
      date = item.start_date ? `Started ${formatDay(item.start_date)}` : "Not started yet";
    } else if (item.purchase_date) {
      // Ongoing: no start to pick, the member has it from the day access was given.
      date = `Joined ${formatDay(item.purchase_date)}`;
    }
    return (
      <Box sx={metaSx}>
        {date ? (
          <Box component="span" sx={metaItemSx}>
            <GrIcon icon={icons.calendar} size="sm" />
            {date}
          </Box>
        ) : null}
        <Box component="span" sx={{ ...metaItemSx, ml: "auto" }}>
          <Box component="img" src={coin} alt="" width={16} height={16} sx={coinSx} />
          {coinsLabel(item.total_points_earned)}
        </Box>
      </Box>
    );
  };

  const contentButton =
    buttonText && buttonLink ? (
      <GrButton
        variant="secondary"
        onClick={() => window.open(buttonLink, "_blank", "noopener")}
      >
        {buttonText}
      </GrButton>
    ) : null;

  return (
    <GrPage>
      <GrPageHeader
        title={intro.title || "Programmes"}
        subtitle={intro.text || "Your programmes. Open one to see its calendar."}
        actions={
          noProgrammes ? (
            contentButton
          ) : (
            <Box sx={headerActionsSx}>
              <ProgrammeSearchBar search={search} />
              {contentButton}
            </Box>
          )
        }
      />

      {isLoading ? (
        <GrLoader label="Loading your programmes" />
      ) : noProgrammes ? (
        <GrCard>
          <GrEmptyState
            icon={<GrIcon icon={icons.activity} />}
            title="No programmes yet"
            text="When your gym gives you a programme, it shows up here."
          />
        </GrCard>
      ) : (
        <>
          {search.total === 0 ? <ProgrammeNoMatch search={search} /> : null}
          <Box sx={gridSx}>
            {search.visible.map((item, index) => {
              const programme = item._id || {};
              const open = canOpenCalendar(item);
              const image = programme.program_images?.thumbnail_2 || programme.program_images?.thumbnail_1;
              const title = htmlText(item.title || programme.title) || "Programme";
              return (
                <GrMediaCard
                  eagerImage={isFirstRow(index)}
                  key={programme._id || title}
                  image={image ? s3baseUrl + image : null}
                  imageAlt={title}
                  placeholderIcon={icons.activity}
                  title={title}
                  description={programme.short_description}
                  onMoreDescription={() => openDetails(item)}
                  meta={meta(item)}
                  onClick={open ? () => openCalendar(item) : undefined}
                  actions={
                    open ? (
                      <GrButton
                        variant="outline"
                        size="sm"
                        startIcon={<GrIcon icon={icons.calendar} size="sm" />}
                        sx={cardButtonSx}
                        onClick={() => openCalendar(item)}
                      >
                        Open programme
                      </GrButton>
                    ) : (
                      <GrButton
                        variant="outline"
                        size="sm"
                        startIcon={<GrIcon icon={icons.playArrow} size="sm" />}
                        sx={cardButtonSx}
                        onClick={() => {
                          setStartFor(item);
                          setStartOpen(true);
                        }}
                      >
                        Start programme
                      </GrButton>
                    )
                  }
                />
              );
            })}
          </Box>
          <ProgrammeLoadMore search={search} />
        </>
      )}

      <ConfirmationWithDate
        open={startOpen}
        programId={startFor}
        setOpen={setStartOpen}
        reload={refetch}
      />
    </GrPage>
  );
}
